import type { BannerOutput } from "@/api/custom_types";

import { useTranslation } from "@/hooks/useTranslation";
import { useAcknowledgeBanner, useGetActiveBanners } from "@/services";

import { currentUserAtom } from "@/store/login";

import { Button, Icon, Typography } from "@st-marketplace/ui";

import { useAtomValue } from "jotai";

interface BannerItemProps {
	banner: BannerOutput;
	onDismiss: () => void;
}

const BannerItem = ({ banner, onDismiss }: BannerItemProps) => {
	const { t } = useTranslation();

	return (
		<div
			role="status"
			className="flex flex-col gap-3 rounded-xl border border-primary-border/40 bg-primary-300 p-4 sm:flex-row sm:items-center sm:gap-4"
		>
			<Icon
				name="information-line"
				size="md"
				className="shrink-0 self-start text-primary-100 sm:self-center"
			/>

			<div className="min-w-0 flex-1">
				<Typography
					variant="caption"
					className="font-semibold tracking-wider text-secondary-text-100 uppercase"
				>
					{t("banner.eyebrow")}
				</Typography>

				<Typography
					variant="body-md"
					as="p"
					className="mt-0.5 font-medium text-secondary-text"
				>
					{banner.text}
				</Typography>
			</div>

			<Button
				type="button"
				size="sm"
				color="secondary"
				className="shrink-0 self-start sm:self-center"
				onClick={onDismiss}
			>
				{t("banner.dismiss")}
			</Button>
		</div>
	);
};

const NotificationBanner = () => {
	const { t } = useTranslation();
	const user = useAtomValue(currentUserAtom);

	const { data: banners = [] } = useGetActiveBanners({
		query: {
			user_id: user?.id,
		},
	});

	const acknowledgeBanner = useAcknowledgeBanner();

	if (banners.length === 0) {
		return null;
	}

	return (
		<section
			role="region"
			aria-label={t("banner.notifications")}
			className="flex flex-col gap-3 pt-5"
		>
			{banners.map((banner) => (
				<BannerItem
					key={banner.id}
					banner={banner}
					onDismiss={() =>
						acknowledgeBanner.mutate({
							path: { banner_id: banner.id },
							query: { user_id: user?.id },
						})
					}
				/>
			))}
		</section>
	);
};

export default NotificationBanner;
